'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@galapagos/design-system/web';
import { PageTitle } from '../components/PageTitle';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupApi } from '../hooks/useSignupApi';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';
import { startQuietEligibilityCheck } from '../profile/eligibility-poller';
import { buildEligibilityRequest } from '../profile/profile-answers';
import { useSignupLegalName } from '../profile/signup-identity';
import { FIRST_SIGNUP_STEP } from '../steps';
import { useSignupFlowStore, type SignupLegalName } from '../stores/signup-flow-store';
import { ReturnToStep } from './ReturnToStep';
import { EarlierAnswersGate } from './EarlierAnswersGate';

/**
 * How long the member waits for an answer before searching instead. Production
 * checks synchronously; the Galapagos check answers in the background, so this
 * bounds the wait and never holds anyone longer.
 */
export const ELIGIBILITY_CHECK_WAIT_MS = 10_000;

function EligibilityCheckStep({
  legalName,
  birthDate,
}: {
  legalName: SignupLegalName;
  birthDate: string;
}) {
  const { t } = useTranslation('signup');
  const api = useSignupApi();
  const status = useSignupFlowStore((state) => state.eligibilityStatus);
  const { goToStep } = useSignupNavigation();
  const [noAnswer, setNoAnswer] = useState(false);
  const moved = useRef(false);
  const title = t('eligibilityCheck.title');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();

  // Name and birth date only: the member has not named an employer yet.
  useEffect(() => {
    startQuietEligibilityCheck(
      api,
      buildEligibilityRequest({ legalName, dateOfBirth: birthDate }),
      () => setNoAnswer(true),
    );
    const timer = setTimeout(() => setNoAnswer(true), ELIGIBILITY_CHECK_WAIT_MS);
    return () => clearTimeout(timer);
  }, [api, legalName, birthDate]);

  // A match skips the search, as production does; anything else searches.
  useEffect(() => {
    if (moved.current) return;
    if (status === 'eligible') {
      moved.current = true;
      goToStep('substance');
    } else if (status === 'needs_review' || noAnswer) {
      moved.current = true;
      goToStep('employer-search');
    }
  }, [status, noAnswer, goToStep]);

  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} headingRef={headingRef} />
        <Spinner accessibilityLabel={title} />
        <p
          className="signup-page-title__description"
          aria-live="polite"
          data-testid="signup-status"
        >
          {t('eligibilityCheck.status')}
        </p>
      </div>
    </SignupShell>
  );
}

/**
 * `/eligibility-check/`: right after the code, on a link with no
 * employer, checks the name and birth date for a benefit. A match goes
 * straight to substance; no match, or no answer in time, goes to the search.
 */
export function EligibilityCheckStepScreen() {
  const { path } = useSignupNavigation();
  const legalName = useSignupLegalName();
  const birthDate = useSignupFlowStore((state) => state.birthDate);
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  // An employer link never checks here; it starts its own path over.
  if (path !== 'no-employer') return <ReturnToStep step={FIRST_SIGNUP_STEP} />;
  return (
    <EarlierAnswersGate step="eligibility-check">
      {legalName !== null && birthDate !== null ? (
        <EligibilityCheckStep legalName={legalName} birthDate={birthDate} />
      ) : null}
    </EarlierAnswersGate>
  );
}
